import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthThirdPartyCookiesMessage } from '@/components/auth-third-party-cookies-message';
import { RETURN_TO_KEY } from '@/components/auth0-provider';
import { makeApolloClient } from '@/lib/apollo-client';
import { usePostRegisterMutation } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { isOAuthSilentAuthLoginRequired } from '@/lib/utils/auth0-oauth-error';
import { ApolloProvider as RootApolloProvider } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2 } from 'lucide-react';

function PostAuthHandler() {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  const [postRegisterMutation] = usePostRegisterMutation();

  const handlePostAuth = useCallback(async () => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    try {
      setStatus(t('page.redirect.setting_up_account'));
      const { data } = await postRegisterMutation({
        variables: { config: { preferredLanguage: navigator.language } },
      });

      const result = data?.authMutations.postRegister;

      if (result?.__typename === 'DomainException') {
        throw new Error(`PostRegister error: ${result.code}`);
      }

      // Refresh token only if accounts were merged (claims changed)
      const accountMerged =
        result?.__typename === 'MutationSuccess' && result.id === 'merged';
      if (accountMerged) {
        await getAccessTokenSilently({ cacheMode: 'off' });
      }

      trackEvent('USER_SIGNED_IN', { accountMerged });

      // Navigate to stored destination
      const returnTo = localStorage.getItem(RETURN_TO_KEY) || '/';
      localStorage.removeItem(RETURN_TO_KEY);
      navigate(returnTo, { replace: true });
    } catch (err) {
      console.error('Post-authentication error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      trackEvent('USER_SIGN_IN_FAILED', { errorMessage });
      setPageError(errorMessage);
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [getAccessTokenSilently, navigate, postRegisterMutation, t]);

  useEffect(() => {
    if (!hasProcessed.current) {
      handlePostAuth();
    }
  }, [handlePostAuth]);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      {!pageError && (
        <p className="text-sm text-muted-foreground">
          {status || t('page.redirect.completing_sign_in')}
        </p>
      )}
      {pageError && <p className="text-sm text-destructive">{pageError}</p>}
    </div>
  );
}

export default function RedirectPage() {
  const { t } = useTranslation();
  const { error, isLoading, isAuthenticated, getAccessTokenSilently } =
    useAuth0();
  const navigate = useNavigate();
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [oauthSilentFailure, setOauthSilentFailure] = useState(false);

  useEffect(() => {
    trackEvent('REDIRECT_VIEWED');
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getAccessTokenSilently({ cacheMode: 'off' })
        .then(setAuthToken)
        .catch((err: unknown) => {
          console.error('Redirect token error:', err);
          if (isOAuthSilentAuthLoginRequired(err)) {
            setOauthSilentFailure(true);
          } else {
            navigate('/login', { replace: true });
          }
        });
    }
  }, [isLoading, isAuthenticated, getAccessTokenSilently, navigate]);

  useEffect(() => {
    if (error) {
      console.error('Authentication error:', error);
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [error, navigate]);

  const apolloClient = useMemo(
    () =>
      authToken
        ? makeApolloClient(authToken, () => getAccessTokenSilently())
        : null,
    [authToken, getAccessTokenSilently]
  );

  if (isLoading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        {error ? (
          <p className="text-sm text-destructive">
            {t('page.redirect.authentication_failed')}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('page.redirect.completing_sign_in')}
          </p>
        )}
      </div>
    );
  }

  if (oauthSilentFailure) {
    return <AuthThirdPartyCookiesMessage />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        {error ? (
          <p className="text-sm text-destructive">
            {t('page.redirect.authentication_failed')}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('page.redirect.completing_sign_in')}
          </p>
        )}
      </div>
    );
  }

  if (!apolloClient) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {t('page.redirect.completing_sign_in')}
        </p>
      </div>
    );
  }

  return (
    <RootApolloProvider client={apolloClient}>
      <PostAuthHandler />
    </RootApolloProvider>
  );
}
